// Lets visitors move the floating accessibility button up and down so it never covers page content
// (for example the footer links). It still starts exactly where it always has.
//
// Ways to move it, so nobody is forced to drag (WCAG 2.5.7 "Dragging Movements"):
//   - pointer: drag it (mouse, touch or pen);
//   - keyboard: focus it and press Up / Down (Shift = bigger steps); Enter or Space still opens the panel;
//   - single pointer, no dragging: "Move up" / "Move down" buttons inside the accessibility panel.
// "Reset to defaults" puts it back. The position is remembered in localStorage.
// The panel follows the button, opening above it, or below it when there is no room above.
const BUTTON_ID = 'accessibility-modal-button';
const PANEL_ID = 'accessibility-modal';
const STORAGE_KEY = 'accessibilityButtonBottom';
const MARGIN = 8; // keep this far from the viewport edges (px)
const GAP = 8; // space between the button and the panel (px)
const DRAG_THRESHOLD = 6; // px of movement before a press counts as a drag, so taps and clicks still open the panel
const STEP = 24; // arrow key
const BIG_STEP = 96; // Shift + arrow key
const BUTTON_STEP = 48; // "Move up" / "Move down" buttons

export function initAccessibilityButton() {
  const button = document.getElementById(BUTTON_ID);
  const panel = document.getElementById(PANEL_ID);
  if (!button || !panel) return;

  const root = document.documentElement;
  let positioned = false; // true once the button has a user-chosen position

  const currentBottom = () => window.innerHeight - button.getBoundingClientRect().bottom;
  const maxBottom = () => Math.max(MARGIN, window.innerHeight - button.offsetHeight - MARGIN);
  const clamp = (value) => Math.min(Math.max(value, MARGIN), maxBottom());

  function store(value) {
    try { localStorage.setItem(STORAGE_KEY, String(Math.round(value))); } catch { /* storage blocked: position just will not persist */ }
  }
  function read() {
    try {
      const value = parseFloat(localStorage.getItem(STORAGE_KEY));
      return Number.isFinite(value) ? value : null;
    } catch { return null; }
  }

  // Open the panel above the button; if that does not fit, below it; if neither fits (a short phone screen), use
  // whichever side has more room and shrink the panel to it, scrolling inside, so it never covers the button.
  function positionPanel() {
    const buttonBottom = currentBottom();
    // The panel's natural content height (scrollHeight), not its rendered height: once it has been shrunk to fit, the
    // rendered height would make it look like it fits and it would grow back off-screen. Hidden panels measure 0, so
    // assume a typical height then.
    const panelHeight = panel.scrollHeight || 440;
    const above = buttonBottom + button.offsetHeight + GAP;
    const roomAbove = window.innerHeight - above - MARGIN;
    const roomBelow = buttonBottom - GAP - MARGIN;
    let panelBottom;
    let maxHeight = null; // null = leave the CSS default (the whole viewport height minus a margin)
    if (panelHeight <= roomAbove) {
      panelBottom = above;
    } else if (panelHeight <= roomBelow) {
      panelBottom = buttonBottom - GAP - panelHeight;
    } else if (roomAbove >= roomBelow) {
      panelBottom = above;
      maxHeight = roomAbove;
    } else {
      panelBottom = MARGIN;
      maxHeight = roomBelow;
    }
    root.style.setProperty('--a11y-panel-bottom', `${Math.round(panelBottom)}px`);
    if (maxHeight === null) root.style.removeProperty('--a11y-panel-max-height');
    else root.style.setProperty('--a11y-panel-max-height', `${Math.max(120, Math.round(maxHeight))}px`);
  }

  function setBottom(value, { persist = false } = {}) {
    const next = clamp(value);
    positioned = true;
    button.classList.add('is-moved');
    root.style.setProperty('--a11y-button-bottom', `${Math.round(next)}px`);
    positionPanel();
    if (persist) store(next);
  }

  function reset() {
    positioned = false;
    button.classList.remove('is-moved');
    root.style.removeProperty('--a11y-button-bottom');
    root.style.removeProperty('--a11y-panel-bottom');
    root.style.removeProperty('--a11y-panel-max-height');
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }

  // --- pointer dragging ---------------------------------------------------------------------------------------
  let drag = null;
  let suppressClick = false;

  button.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    drag = { id: event.pointerId, startY: event.clientY, startBottom: currentBottom(), moved: false };
    // Capture right away, not once the drag has started: a fast flick can jump further than the button is tall
    // between two pointer events, and without capture the pointer would already have left the button.
    try { button.setPointerCapture(event.pointerId); } catch { /* the pointer may already be gone */ }
  });
  button.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.id) return;
    const dy = event.clientY - drag.startY;
    if (!drag.moved) {
      if (Math.abs(dy) < DRAG_THRESHOLD) return;
      drag.moved = true;
      button.classList.add('is-dragging');
    }
    setBottom(drag.startBottom - dy);
  });
  const endDrag = () => {
    if (!drag) return;
    if (drag.moved) {
      suppressClick = true; // the click that follows a drag must not toggle the panel
      setTimeout(() => { suppressClick = false; }, 50);
      store(currentBottom());
      button.classList.remove('is-dragging');
    }
    drag = null;
  };
  button.addEventListener('pointerup', endDrag);
  button.addEventListener('pointercancel', endDrag);
  // While the button is being dragged, never let the browser start a native drag (of a link or image that happens to sit
  // under the pointer, such as the header logo when the button is parked at the top). It would cancel the pointer
  // mid-drag and leave the button stuck partway.
  document.addEventListener('dragstart', (event) => { if (drag) event.preventDefault(); }, true);
  // Capture phase, so it runs before Alpine's @click on the same button.
  button.addEventListener('click', (event) => {
    if (!suppressClick) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    suppressClick = false;
  }, true);

  // --- keyboard -----------------------------------------------------------------------------------------------
  const move = (delta) => setBottom(currentBottom() + delta, { persist: true });
  button.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    const size = event.shiftKey ? BIG_STEP : STEP;
    move(event.key === 'ArrowUp' ? size : -size);
  });

  // --- "Move up / Move down" buttons in the panel (no dragging needed) -------------------------------------------
  panel.querySelectorAll('[data-a11y-move]').forEach((control) => {
    control.addEventListener('click', () => move(control.dataset.a11yMove === 'up' ? BUTTON_STEP : -BUTTON_STEP));
  });
  document.getElementById('accessibility-reset')?.addEventListener('click', reset);

  // --- keep things inside the viewport ------------------------------------------------------------------------
  window.addEventListener('resize', () => { if (positioned) setBottom(currentBottom()); });
  // The panel's size changes when it opens (display:none -> block), so re-place it then.
  new ResizeObserver(() => { if (positioned) positionPanel(); }).observe(panel);

  const saved = read();
  if (saved !== null) setBottom(saved);
}
